import { useMemo, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal, Form, Select, Input, InputNumber, Switch, Space, Button as AntButton, Upload, Image, message } from 'antd'
import PageTable from 'components/PageTable'
import SortableColumnTitle from 'components/SortableColumnTitle'
import Button from 'components/Button'
import Icon from 'components/Icon'
import { ICONS } from 'utils/constants'
import {
  getSubscriptions,
  createSubscription,
  updateSubscription,
  getPlans,
  getSubscription,
} from 'redux/actions/subscriptions'
import { fetchLovChildrenByParentName } from 'api/lov'
import { getProducts } from 'api/products'
import useClient from 'hooks/useClient'
import { PERMISSIONS } from 'utils/constants'
import { useDispatch, useSelector } from 'react-redux'
import { notify } from 'reapop'
import { uploadBulkFiles } from 'api/files'

const Subscriptions = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { isClientCan } = useClient()
  const canCreate = isClientCan(PERMISSIONS.CREATE_SUBSCRIPTIONS)
  const canUpdate = isClientCan(PERMISSIONS.UPDATE_SUBSCRIPTIONS)
  const { items: subs, plans, item: subscriptionDetail, isGetLoading, isChangeLoading } = useSelector(
    (state) => state.subscriptions,
  )
  const [freqs, setFreqs] = useState([])
  const [products, setProducts] = useState([])
  const [images, setImages] = useState([])
  const [defaultImageId, setDefaultImageId] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editing, setEditing] = useState(null)

  useEffect(() => {
    dispatch(getPlans())
    dispatch(getSubscriptions())
    fetchLovChildrenByParentName('SUBSCRIPTION_FREQUENCY').then(setFreqs).catch(() => {})
    getProducts(500, 1)
      .then((res) => {
        const products = res?.result || res || []
        const filtered = products.filter((product) => product?.category?.name !== 'SUBSCRIPTION')
        const deduped = Array.from(new Map(filtered.map((p) => [p.id, p])).values())
        setProducts(deduped)
      })
      .catch(() => {})
  }, [dispatch])

  const columns = useMemo(
    () => [
      { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
      { title: 'Title (En)', dataIndex: 'titleEn', key: 'titleEn' },
      { title: 'Title (Ar)', dataIndex: 'titleAr', key: 'titleAr' },
      {
        title: (p) => <SortableColumnTitle {...p} title='Plan' id='planId' />,
        dataIndex: ['plan', 'product', 'title'],
        key: 'plan',
        render: (_, r) => r?.plan?.product?.title,
      },
      {
        title: 'Frequency',
        dataIndex: ['frequency', 'descriptionEn'],
        key: 'frequency',
        render: (_, r) => r?.frequency?.descriptionEn || r?.frequency?.name,
      },
      {
        title: 'Discount %',
        dataIndex: 'customDiscount',
        key: 'customDiscount',
      },
      {
        title: 'Total Amount',
        dataIndex: 'totalAmount',
        key: 'totalAmount',
      },
      {
        title: 'Sub Total Amount',
        dataIndex: 'subTotalAmount',
        key: 'subTotalAmount',
      },
      {
        title: 'Visible',
        dataIndex: 'isVisible',
        key: 'isVisible',
        render: (v) => (v ? 'Yes' : 'No'),
      },
    ],
    [],
  )

  const [form] = Form.useForm()

  const openForm = (record) => {
    setEditing(record || null)
    setIsModalOpen(true)
    if (record) {
      dispatch(getSubscription({ id: record.id }))
    }
  }

  useEffect(() => {
    if (isModalOpen) {
      if (editing) {
        // Wait for the full subscription detail (with images) before filling the form
        if (!subscriptionDetail || subscriptionDetail.id !== editing.id) {
          setImages([])
          setDefaultImageId(null)
          return
        }
        form.resetFields()
        form.setFieldsValue({
          planId: subscriptionDetail.planId,
          frequencyId: subscriptionDetail.frequencyId,
          titleEn: subscriptionDetail.titleEn,
          titleAr: subscriptionDetail.titleAr,
          shortTitleEn: subscriptionDetail.shortTitleEn,
          shortTitleAr: subscriptionDetail.shortTitleAr,
          customDiscount: subscriptionDetail.customDiscount,
          isVisible: subscriptionDetail.isVisible,
          products: subscriptionDetail.products?.map((p) => ({ productId: p.productId, quantity: p.quantity })),
        })
        const imgs = (subscriptionDetail.subscriptionImages || []).map((img) => ({
          id: img.image?.id,
          src: img.image?.src,
          isDefault: img.isDefault,
          file: null,
        }))
        setImages(imgs)
        const def = imgs.find((i) => i.isDefault) || imgs[0]
        setDefaultImageId(def ? def.id : null)
      } else {
        form.resetFields()
        setImages([])
        setDefaultImageId(null)
        form.setFieldsValue({
          customDiscount: 0,
          isVisible: true,
          products: undefined,
        })
      }
    }
  }, [editing, form, isModalOpen, subscriptionDetail])

  const handleOk = async () => {
    const values = await form.validateFields()
    let finalImages = []
    if (images.length) {
      const newFiles = images.filter((i) => i.file)
      const existing = images.filter((i) => !i.file)
      let uploaded = []
      if (newFiles.length) {
        try {
          const progressKey = 'subscription-images-upload-progress'
          const entities = await uploadBulkFiles(
            newFiles.map((i) => i.file),
            {
              onProgress: (percent) => {
                message.open({
                  key: progressKey,
                  type: 'loading',
                  duration: 0,
                  content: `Uploading images... ${percent}%`,
                })
              },
            },
          )
          message.destroy(progressKey)
          uploaded = entities.map((ent, idx) => ({
            id: ent.id,
            isDefault: newFiles[idx].isDefault,
          }))
        } catch (err) {
          message.destroy('subscription-images-upload-progress')
          message.error('Image upload failed')
          return
        }
      }
      finalImages = [
        ...existing.map((i) => ({ id: i.id, isDefault: i.isDefault })),
        ...uploaded,
      ]
      if (!finalImages.some((i) => i.isDefault) && finalImages.length) {
        finalImages[0].isDefault = true
      }
    }

    const payload = { ...values, images: finalImages.length ? finalImages : undefined }
    if (editing) {
      dispatch(updateSubscription({ id: editing.id, data: payload }))
    } else {
      dispatch(createSubscription(payload))
    }
    setIsModalOpen(false)
    setEditing(null)
  }

  const handleCancel = () => {
    setIsModalOpen(false)
    setEditing(null)
  }

  const formContent = (
        <Form form={form} layout='vertical'>
          <Form.Item name='titleEn' label='Title (English)' rules={[{ required: true }]}>
            <Input placeholder='English title' />
          </Form.Item>
          <Form.Item name='titleAr' label='Title (Arabic)' rules={[{ required: true }]}>
            <Input placeholder='Arabic title' />
          </Form.Item>
          <Form.Item name='shortTitleEn' label='Short Title (English)' rules={[{ required: true }]}>
            <Input placeholder='Short English title' />
          </Form.Item>
          <Form.Item name='shortTitleAr' label='Short Title (Arabic)' rules={[{ required: true }]}>
            <Input placeholder='Short Arabic title' />
          </Form.Item>
          <Form.Item name='planId' label='Plan' rules={[{ required: true }]}>
            <Select
              showSearch
              options={plans?.map((p) => ({
                label: `${p.product?.title} (${p.preferredPlan?.descriptionEn || p.preferredPlan?.name})`,
                value: p.id,
              })) || []}
            />
          </Form.Item>
          <Form.Item name='frequencyId' label='Frequency' rules={[{ required: true }]}>
            <Select
              showSearch
              options={freqs.map((f) => ({ label: f.descriptionEn || f.name, value: f.id })) || []}
            />
          </Form.Item>
          <Form.Item name='customDiscount' label='Custom Discount %' initialValue={0}>
            <InputNumber min={0} max={100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name='isVisible' label='Visible' valuePropName='checked' initialValue={true}>
            <Switch />
          </Form.Item>
          <Form.List name='products' rules={[{ required: true, message: 'Add at least one product' }]}>
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key: fieldKey, ...restField }) => (
                  <div
                    key={fieldKey}
                    style={{ display: 'flex'}}
                    align='baseline'
                  >
                    <Form.Item
                      {...restField}
                      name={[restField.name, 'productId']}
                      rules={[{ required: true, message: 'Product' }]}
                      style={{ flex: 1 }}
                    >
                      <Select
                        showSearch
                        placeholder='Select product'
                        dropdownMatchSelectWidth={false}
                        style={{ width: '95%' }}
                        optionFilterProp='label'
                        filterOption={(input, option) =>
                          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        options={
                          (products || []).map((p) => ({
                            label: `${p.title} - ${p.sapId ?? ''}`,
                            value: p.id,
                          })) || []
                        }
                      />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[restField.name, 'quantity']}
                      rules={[{ required: true, message: 'Qty' }]}
                    >
                      <InputNumber min={1} max={999} placeholder='Qty' />
                    </Form.Item>
                    <AntButton type='text' danger onClick={() => remove(restField.name)} icon={<Icon icon={ICONS.TRASH} size={16} />} />
                  </div>
                ))}
                <Form.Item>
                  <AntButton type='dashed' onClick={() => add()} block>
                    + Add product
                  </AntButton>
                </Form.Item>
              </>
            )}
          </Form.List>
          <Form.Item label='Images (optional)'>
            <Space direction='vertical' style={{ width: '100%' }}>
              <Upload
                multiple
                showUploadList={false}
                beforeUpload={(file) => {
                  const isImg =
                    file.type === 'image/jpeg' ||
                    file.type === 'image/png' ||
                    file.type === 'image/jpg' ||
                    file.type === 'image/webp'
                  if (!isImg) {
                    message.error('Only JPG/PNG/WEBP files are allowed')
                    return Upload.LIST_IGNORE
                  }
                  const reader = new FileReader()
                  reader.onload = () => {
                    setImages((prev) => {
                      const newId = file.uid
                      const list = [
                        ...prev,
                        { id: newId, src: reader.result, isDefault: prev.length === 0, file },
                      ]
                      if (prev.length === 0) setDefaultImageId(newId)
                      return list
                    })
                  }
                  reader.readAsDataURL(file)
                  return false
                }}
              >
                <AntButton icon={<Icon icon={ICONS.UPLOAD} size={14} />}>Upload images</AntButton>
              </Upload>
              <Space wrap>
                {images
                  .filter((img) => img.src && img.src.trim() !== '')
                  .map((img, idx) => (
                    <div key={img.id} style={{ border: '1px solid #eee', padding: 6, width: 140 }}>
                      <Image
                        src={img.src}
                        alt='img'
                        width={120}
                        height={90}
                        style={{ objectFit: 'cover' }}
                        fallback='data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="120" height="90"></svg>'
                        preview={false}
                      />
                    <Space style={{ marginTop: 8 }}>
                      <AntButton
                        size='small'
                        type={img.id === defaultImageId ? 'primary' : 'default'}
                        onClick={() => {
                          setDefaultImageId(img.id)
                          setImages((prev) =>
                            prev.map((i) => ({ ...i, isDefault: i.id === img.id })),
                          )
                        }}
                      >
                        Default
                      </AntButton>
                      <AntButton
                        size='small'
                        danger
                        onClick={() => {
                          setImages((prev) => {
                            const filtered = prev.filter((i) => i.id !== img.id)
                            if (filtered.length) {
                              if (!filtered.some((f) => f.isDefault)) {
                                filtered[0].isDefault = true
                                setDefaultImageId(filtered[0].id)
                              }
                            } else {
                              setDefaultImageId(null)
                            }
                            return filtered
                          })
                        }}
                      >
                        Delete
                      </AntButton>
                    </Space>
                    </div>
                  ))}
              </Space>
            </Space>
          </Form.Item>
        </Form>
  )

  return (
    <>
    <PageTable
      pageTitle='Subscriptions'
      baseData={subs}
      columns={columns}
      loading={isGetLoading || isChangeLoading}
      action={
        canCreate ? (
          <Button type='primary' onClick={() => openForm(null)}>
            {t('app.add')}
          </Button>
        ) : null
      }
      onRow={
        canUpdate
          ? (record) => ({
              onClick: () => openForm(record),
            })
          : undefined
      }
    />
    <Modal
      title={editing ? 'Edit Subscription' : 'Create Subscription'}
      open={isModalOpen}
      onOk={handleOk}
      onCancel={handleCancel}
      width={720}
      destroyOnClose
      okText={t('app.save')}
      cancelText={t('app.cancel')}
    >
      {formContent}
    </Modal>
    </>
  )
}

export default Subscriptions
